import React, { useEffect, useState } from 'react';
import './AuthPage.css';
import { apiJson } from '../utils/apiClient';

function AuthPage({ user, onAuthenticated }) {
  const [mode, setMode] = useState(user ? 'verify' : 'login');
  const [email, setEmail] = useState(user?.email || '');
  const [username, setUsername] = useState(user?.username || '');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [message, setMessage] = useState(user ? 'Check your email for a verification link.' : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verify = params.get('verify');
    const reset = params.get('reset');
    if (verify) {
      setToken(verify);
      setMode('verify');
    } else if (reset) {
      setToken(reset);
      setMode('reset-confirm');
    }
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (mode === 'register') {
        const data = await apiJson('/auth/register', {
          method: 'POST',
          body: JSON.stringify({ email, username, password }),
        });
        onAuthenticated(data.user);
        setMode('verify');
        setMessage(data.message || 'Check your email for a verification link.');
      } else if (mode === 'login') {
        const data = await apiJson('/auth/login', {
          method: 'POST',
          body: JSON.stringify({ emailOrUsername: email || username, password }),
        });
        onAuthenticated(data.user);
        if (!data.user?.emailVerified) {
          setMode('verify');
          setMessage('Check your email for a verification link.');
        }
      } else if (mode === 'verify') {
        const data = await apiJson('/auth/verify-email', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });
        onAuthenticated(data.user);
        setMessage('Email verified.');
      } else if (mode === 'reset') {
        await apiJson('/auth/request-password-reset', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        setMode('reset-confirm');
        setMessage('If that account exists, a reset link has been sent.');
      } else if (mode === 'reset-confirm') {
        await apiJson('/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify({ token, password }),
        });
        setMode('login');
        setPassword('');
        setToken('');
        setMessage('Password reset. Log in with the new password.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-panel">
        <div className="auth-panel__brand">
          <span className="auth-panel__icon">🎸</span>
          <h1>Chord Detector</h1>
        </div>
        <form className="auth-form" onSubmit={submit}>
          {mode === 'register' ? (
            <label>
              Username
              <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
            </label>
          ) : null}
          {mode !== 'verify' && mode !== 'reset-confirm' ? (
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
            </label>
          ) : null}
          {mode === 'verify' || mode === 'reset-confirm' ? (
            <label>
              Token
              <input value={token} onChange={(e) => setToken(e.target.value)} autoComplete="one-time-code" required />
            </label>
          ) : null}
          {mode === 'login' || mode === 'register' || mode === 'reset-confirm' ? (
            <label>
              Password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required />
            </label>
          ) : null}
          {error ? <div className="auth-message auth-message--error">{error}</div> : null}
          {message ? <div className="auth-message">{message}</div> : null}
          <button className="auth-submit" type="submit" disabled={busy}>
            {mode === 'register' ? 'Create Account' :
              mode === 'verify' ? 'Verify Email' :
              mode === 'reset' ? 'Send Reset Email' :
              mode === 'reset-confirm' ? 'Reset Password' : 'Log In'}
          </button>
        </form>
        {mode === 'verify' && user ? (
          <button
            className="auth-resend"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await apiJson('/auth/resend-verification', { method: 'POST' });
                setMessage('Verification email sent.');
              } catch (err) {
                setError(err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Resend verification email
          </button>
        ) : null}
        <div className="auth-links">
          {mode !== 'login' ? <button onClick={() => setMode('login')}>Log in</button> : null}
          {mode !== 'register' ? <button onClick={() => setMode('register')}>Create account</button> : null}
          {mode !== 'reset' ? <button onClick={() => setMode('reset')}>Forgot password</button> : null}
          {mode !== 'verify' ? <button onClick={() => setMode('verify')}>Verify email</button> : null}
        </div>
      </section>
    </main>
  );
}

export default AuthPage;
